import type { RootStackParamList } from '../../../../../../navigation/types';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';

import { TextLink as TextLinkImpl, type TextLinkProps } from '../../../../../../../../mads/TextLink';

import { useNavigation } from '@react-navigation/native';
import React, { useCallback } from 'react';
import { Platform } from 'react-native';

export interface ProfileLinkProps extends TextLinkProps {
    targetUrl: string;
}

/**
 * The inline "profile" link shown in View Job's Profile Insights.
 *
 * On native, tapping pushes an in-app Profile web view onto View Job's own
 * navigation stack, so View Job stays mounted underneath and Back returns to it
 * (RN-4845). Routing through the native `navigateView` bridge (as other View Job
 * links do) would send Profile through the app's tab router, which dismisses the
 * View Job sheet and, on bottom-nav markets, bounces the user from search results
 * back to Home.
 *
 * On web there is no presented sheet to preserve, so we keep the existing
 * behaviour of opening the profile URL in a new tab.
 */
export function ProfileLink({ targetUrl, onPress, ...rest }: ProfileLinkProps): React.JSX.Element {
    const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();

    const handleNativePress = useCallback(() => {
        onPress?.();
        if (targetUrl) {
            navigation.navigate('ProfileWebView', { url: targetUrl });
        }
    }, [navigation, onPress, targetUrl]);

    if (Platform.OS === 'web') {
        return (
            <TextLinkImpl
                {...rest}
                onPress={onPress}
                targetUrl={targetUrl}
                webHrefAttrs={{ target: '_blank' }}
            />
        );
    }

    return <TextLinkImpl {...rest} onPress={handleNativePress} />;
}
